import { useCallback, useState } from 'react';
import { interviewReadinessAPI, enrollmentsAPI, batchesAPI } from '../services/api';
import { normalizeApiListResponse } from '../utils/apiListNormalize';

const buildReadinessMap = (enrollments, readinessResults) => {
  const map = {};
  readinessResults.forEach((result, index) => {
    if (result?.data) {
      map[enrollments[index].id] = result.data;
    }
  });
  return map;
};

const useInterviewReadinessData = ({ execute }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [readinessMap, setReadinessMap] = useState({});

  const fetchData = useCallback(async () => {
    await execute(async () => {
      const [enrollmentsRes, batchesRes] = await Promise.all([
        enrollmentsAPI.getAll(),
        batchesAPI.getAll()
      ]);

      const enrollmentsData = enrollmentsRes.data || [];
      const batchesData = normalizeApiListResponse(batchesRes.data);
      setEnrollments(enrollmentsData);
      setBatches(batchesData);

      const readinessPromises = enrollmentsData.map((enrollment) =>
        interviewReadinessAPI.getByEnrollment(enrollment.id).catch(() => null)
      );
      const readinessResults = await Promise.all(readinessPromises);
      setReadinessMap(buildReadinessMap(enrollmentsData, readinessResults));

      return { enrollments: enrollmentsData, batches: batchesData };
    });
  }, [execute]);

  const getReadinessStatus = useCallback(
    (enrollmentId) => {
      const readiness = readinessMap[enrollmentId];
      if (!readiness) return { status: 'no-data', label: 'No Data', color: 'bg-gray-100 text-gray-600' };
      if (readiness.interviewReady === true) return { status: 'ready', label: 'Ready', color: 'bg-green-100 text-green-700' };
      if (readiness.interviewReady === false) return { status: 'not-ready', label: 'Not Ready', color: 'bg-red-100 text-red-700' };
      return { status: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-700' };
    },
    [readinessMap]
  );

  return {
    enrollments,
    batches,
    readinessMap,
    fetchData,
    getReadinessStatus
  };
};

export default useInterviewReadinessData;
